import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { getQaSelector } from '../../../../utils/common'
import Button from '../../../Button'
import { PageEmptyState } from '../../../PageEmptyState'
import { getViewingProfileLink } from '../../../RentalPortalViewing'
import { Table } from '../../../Table'
import { TablePagination } from '../../../TablePagination'
import Text from '../../../Text'
import { useApplicantProfileContext } from '../RentalPortalApplicantProfile.hooks'
import { RentalPortalApplicantProfileTabShell } from '../RentalPortalApplicantProfileTabShell'

import { ApplicantProfileViewingsRow } from './ApplicantProfileViewingsRow'
import {
  APPLICANT_PROFILE_VIEWINGS_BOOK_ACTION,
  APPLICANT_PROFILE_VIEWINGS_SKELETON_ROWS,
  APPLICANT_VIEWING_COLUMNS_CONFIG,
  QA_NAME_APPLICANT_PROFILE_VIEWINGS,
  QA_NAME_APPLICANT_PROFILE_VIEWINGS_RETRY,
  QA_NAME_APPLICANT_PROFILE_VIEWINGS_TABLE,
} from './RentalPortalApplicantProfileViewings.constants'
import { useApplicantProfileViewings } from './RentalPortalApplicantProfileViewings.hooks'
import {
  getApplicantViewingItemActions,
  getApplicantViewingsState,
  getIsApplicantViewingsEmpty,
  getIsApplicantViewingsError,
} from './RentalPortalApplicantProfileViewings.utils'

import * as styles from './RentalPortalApplicantProfileViewings.module.css'

import RefreshIcon from '../../../../icons/refresh.svg'
import RentalPortalIcon from '../../../../icons/rental-portal.svg'
import ViewingIcon from '../../../../icons/viewing.svg'

function RentalPortalApplicantProfileViewings() {
  const { t } = useTranslation()

  const { applicantData } = useApplicantProfileContext()

  const navigate = useNavigate()

  const containerRef = useRef(null)

  function handleCallAction(action, item) {
    navigate(getViewingProfileLink(item.id))
  }

  const {
    handleRefresh,
    handleSetPagination,
    hasError,
    isInitiated,
    isLoading,
    items,
    pagination,
  } = useApplicantProfileViewings({ applicantId: applicantData?.id })

  const state = getApplicantViewingsState({
    hasError,
    isInitiated,
    viewings: items,
  })

  return (
    <div
      {...getQaSelector({ value: QA_NAME_APPLICANT_PROFILE_VIEWINGS })}
      className={styles.RentalPortalApplicantProfileViewings}
      ref={containerRef}
    >
      {getIsApplicantViewingsError(state) ? (
        <PageEmptyState EntityIcon={RentalPortalIcon}>
          <Text isSecondary>
            {t('RentalPortal.ApplicantProfileViewingsLoadErrorKey')}
          </Text>
          <Button.Flat
            beforeIconProps={{ Icon: RefreshIcon }}
            isLoading={isLoading}
            qaName={QA_NAME_APPLICANT_PROFILE_VIEWINGS_RETRY}
            text={t('RentalPortal.ListingRetryBtnKey')}
            onClick={handleRefresh}
          />
        </PageEmptyState>
      ) : getIsApplicantViewingsEmpty(state) ? (
        <RentalPortalApplicantProfileTabShell
          EntityIcon={ViewingIcon}
          action={APPLICANT_PROFILE_VIEWINGS_BOOK_ACTION}
          textKey='RentalPortal.ApplicantProfileViewingsEmptyKey'
        />
      ) : (
        <>
          <Table
            TableRow={ApplicantProfileViewingsRow}
            actionsColumnWidth={0}
            className={styles.table}
            columns={APPLICANT_VIEWING_COLUMNS_CONFIG}
            containerRef={containerRef}
            context={QA_NAME_APPLICANT_PROFILE_VIEWINGS_TABLE}
            headerRowProps={{ className: styles.row, resizable: false }}
            isLoading={isLoading}
            items={items}
            rowProps={{
              getActions: getApplicantViewingItemActions,
              onCallAction: handleCallAction,
            }}
            skeletonProps={{
              headerRowClassName: styles.skeletonHeaderRow,
              rowClassName: styles.skeletonRow,
              withCheckbox: false,
            }}
            skeletonRows={APPLICANT_PROFILE_VIEWINGS_SKELETON_ROWS}
          />
          <TablePagination
            responsive
            containerRef={containerRef}
            isSkeleton={!isInitiated}
            value={pagination}
            onChange={handleSetPagination}
          />
        </>
      )}
    </div>
  )
}

export default RentalPortalApplicantProfileViewings
